import { createAsyncThunk } from '@reduxjs/toolkit';
import { LOCATIONS_LIST_TAG } from 'modules/locations/store/locations-api';
import { baseApi } from 'shared/api/base-api';
import { FetchLocationMapImage, UpsertLocationInput } from '../domain/interfaces/actions';
import { Image, Location } from '../domain/interfaces/location';
import locationsRepository from '../domain/repositories/locations.repository';
import locationsResource from '../domain/resources/locations.resource';

const PREFIX = 'locations';

const handleError = (error: unknown, prefix: string) => {
  console.error(`${prefix} error:`, error);
  const message = error instanceof Error ? error.message : String(error);
  return message;
};
/**
 * Сбрасывает перечень локаций. Отдельно от тега с `id`: в RTK Query они не
 * пересекаются, поэтому появление и удаление локации надо объявлять именно тегом
 * списка. Мутации здесь thunk'и — сами теги не инвалидируются.
 */
export const invalidateLocationsList = () => baseApi.util.invalidateTags([LOCATIONS_LIST_TAG]);

const deleteLocationImages = async (locationId: number, images: Image[]) => {
  if (images?.length) {
    await Promise.all(images.map((image) => locationsResource.deleteImage(locationId, image.id)));
  }
};

export const deleteLocation = createAsyncThunk<{ id: number }, { id: number }>(
  `${PREFIX}/deleteLocation`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const location = await locationsRepository.getLocation(payload.id);
      await deleteLocationImages(location.id, location.images);
      await locationsRepository.deleteLocation(payload.id);

      dispatch(invalidateLocationsList());

      return { id: payload.id };
    } catch (error) {
      return rejectWithValue(handleError(error, `${PREFIX}/deleteLocation`));
    }
  },
);

export const upsertLocation = createAsyncThunk<Location, UpsertLocationInput>(
  `${PREFIX}/upsertLocation`,
  async (input, { rejectWithValue, dispatch }) => {
    try {
      const { images = [], tournamentId, ...locationData } = input;
      let location = await locationsRepository.upsertLocation({ ...locationData, tournamentId }, tournamentId);

      if (location.id) {
        location.images = await processLocationImages(location, images);
      }

      // И создание, и правка локации меняют строку в перечне — перечитываем список.
      dispatch(invalidateLocationsList());

      return location;
    } catch (error) {
      return rejectWithValue(handleError(error, `${PREFIX}/upsertLocation`));
    }
  },
);

interface UploadedImage {
  id: number;
  externalUrl: string;
  ordinalNumber: number;
  filename: string;
}

interface UploadedFile {
  img: File;
  index: number;
}

const processLocationImages = async (location: Location, images: (Image | File)[]) => {
  // Разделяем изображения по типу
  const existingUrls = images.filter((img): img is Image => typeof img === 'object' && 'id' in img);
  const newFiles: UploadedFile[] = images
    .map((img, index) => ({ img, index }))
    .filter((file): file is UploadedFile => file.img instanceof File);

  // Обработка изображений
  await deleteUnusedImages(location, existingUrls);
  await uploadNewImages(location.id, newFiles);
  await updateImagesOrder(location, images);
  return (await locationsRepository.getLocationImages(location.id)).images;
};

const deleteUnusedImages = async (location: Location, existingUrls: Image[]) => {
  const existingIds = existingUrls.map((img) => img.id);
  const imagesToDelete = location.images.filter((img) => !existingIds.includes(img.id));
  await deleteLocationImages(location.id, imagesToDelete);
};

const uploadNewImages = async (locationId: number, files: UploadedFile[]): Promise<UploadedImage[]> => {
  if (!files.length) return [];

  return Promise.all(
    files.map(async (file) => {
      const result = await locationsResource.uploadImage(file.img, locationId, file.index);
      return {
        id: result.id,
        externalUrl: result.image,
        ordinalNumber: result.ordinalNumber,
        filename: file.img.name,
      };
    }),
  );
};

const updateImagesOrder = async (location: Location, images: (Image | File)[]) => {
  const reorderedImages = images.reduce<{ imageId: number; ordinalNumber: number }[]>((acc, url, index) => {
    if (typeof url === 'object' && 'id' in url) {
      const img = location.images.find((i) => i.id === url.id);
      if (img && img.ordinalNumber !== index) {
        acc.push({ imageId: img.id, ordinalNumber: index });
      }
    }

    return acc;
  }, []);

  if (reorderedImages.length > 0) {
    await locationsResource.updateImagesOrder(location.id, reorderedImages);
  }
};

export const fetchLocationMapImage = createAsyncThunk<string | null, FetchLocationMapImage>(
  `${PREFIX}/fetchLocationMapImage`,
  async (payload, { rejectWithValue }) => {
    try {
      return await locationsRepository.getLocationMapImage(payload.locationId, payload.width, payload.height);
    } catch (error) {
      return rejectWithValue(handleError(error, `${PREFIX}/fetchLocationMapImage`));
    }
  },
);

export const fetchLocation = createAsyncThunk<Location, { id: number }>(
  `${PREFIX}/fetchLocation`,
  async (payload, { rejectWithValue }) => {
    try {
      return await locationsRepository.getLocation(payload.id);
    } catch (error) {
      return rejectWithValue(handleError(error, `${PREFIX}/fetchLocation`));
    }
  },
);
